import { Dialog, Fluent } from "@serenity-is/corelib";

const IDLE_MS       = (window as any).__E2E_IDLE_MS   ?? (20 * 60 * 1000);  // 20 minutes idle before warning
const WARN_SECS     = (window as any).__E2E_WARN_SECS ?? 60;               // 1 minute to respond
const KEEPALIVE_URL = '/Account/KeepAlive';
const SIGNOUT_URL   = '/Account/Signout';

/** Shows a session-expiry warning dialog and manages the countdown. */
class SessionWarningDialog {
    private dialog:   Dialog | null = null;
    private interval: ReturnType<typeof setInterval> | null = null;
    private countdownEl: HTMLElement | null = null;

    get isOpen(): boolean {
        return this.dialog !== null;
    }

    open(onStay: () => void, onSignOut: () => void): void {
        if (this.dialog) return;

        let remaining = WARN_SECS;

        this.countdownEl = Fluent("span")
            .append(String(remaining))
            .getNode();

        const progressBar = Fluent("div")
            .attr("role", "progressbar")
            .style(s => {
                s.width           = "100%";
                s.height          = "100%";
                s.transition      = "width 1s linear";
                s.backgroundColor = "var(--bs-primary, #6c90b0)";
                s.borderRadius    = "4px";
                s.backgroundImage = "linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)";
                s.backgroundSize  = "1rem 1rem";
            })
            .getNode();

        const body = Fluent("div")
            .class("py-2")
            .append(
                Fluent("p").class("mb-2")
                    .append("Your session is about to expire.")
            )
            .append(
                Fluent("p").class("mb-3")
                    .append("You will be logged off in ")
                    .append(this.countdownEl)
                    .append(" seconds due to inactivity.")
            )
            .append(
                Fluent("div")
                    .style(s => {
                        s.height          = "10px";
                        s.backgroundColor = "#e0e0e0";
                        s.borderRadius    = "4px";
                        s.overflow        = "hidden";
                    })
                    .append(progressBar)
            )
            .getNode();

        this.dialog = new Dialog({
            title:         "Session Expiration Warning!",
            preferBSModal: true,
            backdrop:      "static",
            closeButton:   false,
            closeOnEscape: false,
            centered:      true,
            size:          "sm",
            autoOpen:      false,
            element:       body,
            buttons: [
                {
                    text:     "Signout",
                    cssClass: "btn-danger",
                    click:    () => { this.close(); onSignOut(); }
                },
                {
                    text:     "Stay Logged In",
                    cssClass: "btn-primary",
                    click:    () => { this.close(); onStay(); }
                }
            ]
        });

        this.dialog.open();

        // Round the modal corners after Bootstrap renders it
        requestAnimationFrame(() => {
            const modalContent = body.closest('.modal-content') as HTMLElement | null;
            if (modalContent) {
                modalContent.style.borderRadius = '12px';
                modalContent.style.overflow     = 'hidden';
            }
        });

        this.interval = setInterval(() => {
            remaining--;
            if (this.countdownEl) this.countdownEl.textContent = String(remaining);
            progressBar.style.width = `${(remaining / WARN_SECS) * 100}%`;
            if (remaining <= 0) {
                this.clearInterval();
                onSignOut();
            }
        }, 1000);
    }

    close(): void {
        this.clearInterval();
        this.dialog?.close();
        this.dialog = null;
        this.countdownEl = null;
    }

    private clearInterval(): void {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }
}

/** Initialises idle detection and the session-expiry warning. Call once on page load. */
export function initIdleTimeoutHandler(): void {
    // Skip on pages where the user is not authenticated
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith('/account/login') || path.startsWith('/account/accessdenied')) return;

    const warning = new SessionWarningDialog();
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    function stayLoggedIn(): void {
        fetch(KEEPALIVE_URL, { credentials: 'same-origin' }).catch(() => {});
        resetIdleTimer();
    }

    function signOut(): void {
        window.location.href = SIGNOUT_URL;
    }

    function showWarning(): void {
        warning.open(stayLoggedIn, signOut);
    }

    function resetIdleTimer(): void {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(showWarning, IDLE_MS);
    }

    // Reset the idle clock on any user interaction (but not while the warning is showing)
    const activityEvents: (keyof DocumentEventMap)[] =
        ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(ev =>
        document.addEventListener(ev, () => {
            if (!warning.isOpen) resetIdleTimer();
        }, { passive: true })
    );

    resetIdleTimer();
}
